import React, { useEffect, useState, useRef } from "react";
import { Rnd } from "react-rnd";

const ResizeAndDraggable = ({
  defaultValue = {
    x: 0,
    y: 0,
    width: 270,
    height: 323,
  },
  minWidth = 270,
  minHeight = 323,
  children,
  aspectRatio = true,
  resize = (data) => data,
  resizeEnd,
  bounds = "window",
  onFocusValue = false,
  showAccordin,
  setDirectionResizing,
  page,
  center = true,
  ...props
}) => {
  const calculateInitialDimensions = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    return {
      width: Math.floor(screenWidth * 0.7),
      height: Math.floor(screenHeight * 0.7),
    };
  };

  const initialDimensions = calculateInitialDimensions();
  
  const calculateInitialPosition = () => {
    const widgetWidth = defaultValue?.width || dimensions.width;
    const widgetHeight = defaultValue?.height || dimensions.height;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    if (center) {
      const x = Math.floor((windowWidth - widgetWidth) / 2);
      const y = Math.floor((windowHeight - widgetHeight) / 2) -100;
      return { x, y };
    }
    return {
      x: 0,
      y: 0,
    };
  };

  const initialPosition = calculateInitialPosition();
  
  const [dimensions, setDimensions] = useState({
    width: initialDimensions.width,
    height: initialDimensions.height,
  });
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  const [minHeightChecker, setMinCheck] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasStartedDragging, setHasStartedDragging] = useState(false);
  const rndRef = useRef(null);
  const [previousPosition, setPreviousPosition] = useState({
    x: Math.floor((Math.random() * (window.innerWidth - minWidth)) / 2),
    y: Math.floor((Math.random() * (window.innerHeight - minHeight)) / 2),
  });
  const { x, y, width: defaultWidth, height: defaultHeight, ...others } = defaultValue;

  useEffect(() => {
    resize({ width: dimensions.width, height: dimensions.height });
    setPosition({
      x: 0,
      y: 0,
    });
    setHasStartedDragging(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("draggable-widget-toggle", {
        detail: { target: "widget", open: true },
      })
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("draggable-widget-toggle", {
          detail: { target: "widget", open: false },
        })
      );
    };
  }, []);

  useEffect(() => {
    if (!hasStartedDragging) {
      const style = document.createElement('style');
      style.id = 'rnd-no-transform-initial';
      style.textContent = `
        .rnd-no-transform-initial .react-draggable,
        .rnd-no-transform-initial [class*="react-draggable"],
        .rnd-no-transform-initial > *,
        .rnd-no-transform-initial {
          transform: initial !important;
        }
      `;
      document.head.appendChild(style);

      const resetTransform = () => {
        if (hasStartedDragging) return;
        
        if (rndRef.current) {
          const rndElement = rndRef.current;
          if (rndElement.resizableElement) {
            const draggableEl = rndElement.resizableElement;
            if (draggableEl && draggableEl.style) {
              const computedStyle = window.getComputedStyle(draggableEl);
              const transform = computedStyle.transform;
              if (transform && transform !== 'none' && transform !== 'initial' && transform.includes('translate')) {
                draggableEl.style.setProperty('transform', 'initial', 'important');
              }
            }
          }
          if (rndElement.draggableElement) {
            const draggableEl = rndElement.draggableElement;
            if (draggableEl && draggableEl.style) {
              const computedStyle = window.getComputedStyle(draggableEl);
              const transform = computedStyle.transform;
              if (transform && transform !== 'none' && transform !== 'initial' && transform.includes('translate')) {
                draggableEl.style.setProperty('transform', 'initial', 'important');
              }
            }
          }
        }

        const selectors = [
          '.rnd-no-transform-initial .react-draggable',
          '.rnd-no-transform-initial [class*="react-draggable"]',
          '.rnd-no-transform-initial > *'
        ];
        
        selectors.forEach(selector => {
          try {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              if (el && el.style) {
                const computedStyle = window.getComputedStyle(el);
                const transform = computedStyle.transform;
                if (transform && transform !== 'none' && transform !== 'initial' && transform.includes('translate')) {
                  el.style.setProperty('transform', 'initial', 'important');
                }
              }
            });
          } catch (e) {
            // Ignore selector errors
          }
        });
      };

      const intervalId = setInterval(() => {
        if (!hasStartedDragging) {
          resetTransform();
        } else {
          clearInterval(intervalId);
        }
      }, 5);

      const observer = new MutationObserver(() => {
        if (!hasStartedDragging) {
          resetTransform();
        }
      });

      setTimeout(() => {
        const checkElement = () => {
          const el = document.querySelector('.rnd-no-transform-initial');
          if (el) {
            observer.observe(el, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['style', 'class']
            });
            resetTransform();
          } else {
            setTimeout(checkElement, 10);
          }
        };
        checkElement();
      }, 10);

      return () => {
        clearInterval(intervalId);
        observer.disconnect();
        const existingStyle = document.getElementById('rnd-no-transform-initial');
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [hasStartedDragging]);

  const resizeHandleStyles = {
    top: { display: "ew-resize" },
    bottom: { display: "ew-resize" },
    left: { cursor: "ew-resize" },
    right: { cursor: "ew-resize" },
    topLeft: { cursor: "nwse-resize" },
    topRight: { cursor: "nesw-resize" },
    bottomLeft: { cursor: "nesw-resize" },
    bottomRight: { cursor: "nwse-resize" },
  };

  const generateNewPosition = () => {
    let newX, newY;
    do {
      newX = Math.floor((Math.random() * (window.innerWidth - minWidth)) / 2);
      newY = Math.floor((Math.random() * (window.innerHeight - minHeight)) / 2);
    } while (newX === previousPosition.x && newY === previousPosition.y);

    return { x: newX, y: newY };
  };

  const minHeightForSpecificPage = 44;

  useEffect(() => {
    const checkMinHeight = () => {
      if (showAccordin !== undefined && !showAccordin) {
        if (page === "station" || page === "text" || page === "group") {
          setMinCheck(minHeightForSpecificPage);
          return;
        }
        setMinCheck(0);
        return minHeight;
      } else {
        setMinCheck(minHeight);
      }
    };
    checkMinHeight();
  }, [showAccordin]);

  useEffect(() => {
    if (isResizing || isDragging) {
      document.body.classList.add("isDragging");
      document.body.style.userSelect = "none";
      document.body.style.webkitUserSelect = "none";
      document.body.style.msUserSelect = "none";
      document.body.style.mozUserSelect = "none";
      document.body.style.pointerEvents = "auto";
    } else {
      document.body.classList.remove("isDragging");
      document.body.style.userSelect = "";
      document.body.style.webkitUserSelect = "";
      document.body.style.msUserSelect = "";
      document.body.style.mozUserSelect = "";
    }
  }, [isResizing, isDragging]);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => {
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing]);

  // Add useEffect to monitor and remove react-draggable class
  useEffect(() => {
    const observer = new MutationObserver(() => {
      // Check if class was added
      if (document.body.classList.contains('react-draggable-transparent-selection')) {
        // Remove it immediately
        document.body.classList.remove('react-draggable-transparent-selection');
      }
    });

    // Observe changes to the body classList
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Also remove the class on component mount
  useEffect(() => {
    const removeClass = () => {
      document.body.classList.remove('react-draggable-transparent-selection');
    };
    
    // Remove on mount
    removeClass();
    
    // Also set up an interval to continuously remove it
    const interval = setInterval(() => {
      if (document.body.classList.contains('react-draggable-transparent-selection')) {
        document.body.classList.remove('react-draggable-transparent-selection');
      }
    }, 100);
    
    return () => {
      clearInterval(interval);
    };
  }, []);
  
  // Remove dragging classes from wrapper div and make sure buttons work
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          // If the target has the dragging classes, remove them immediately
          if (target.classList.contains('react-draggable-dragging') || target.classList.contains('react-draggable-dragged')) {
            target.classList.remove('react-draggable-dragging');
            target.classList.remove('react-draggable-dragged');
          }
          // Ensure all buttons inside have pointer events
          const buttons = target.querySelectorAll('button, input, select, a, .MuiButton-root, .ant-btn, [role="button"]');
          buttons.forEach(btn => {
            (btn).style.pointerEvents = 'auto';
            (btn).style.cursor = 'pointer';
          });
        }
      });
    });
    
    // Observe changes to the document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);
  const handleRndRef = (node) => {
    rndRef.current = node;
  };

  useEffect(() => {
    if (hasStartedDragging || !rndRef.current) return;
    
    const forceInitialTransform = () => {
      if (hasStartedDragging || !rndRef.current) return;
      
      try {
        const node = rndRef.current;
        if (node) {
          if (node.resizableElement && node.resizableElement.style && typeof node.resizableElement.style.setProperty === 'function') {
            node.resizableElement.style.setProperty('transform', 'initial', 'important');
          }
          if (node.draggableElement && node.draggableElement.style && typeof node.draggableElement.style.setProperty === 'function') {
            node.draggableElement.style.setProperty('transform', 'initial', 'important');
          }
          const rndElement = node.resizableElement || node.draggableElement;
          if (rndElement && rndElement.style && typeof rndElement.style.setProperty === 'function') {
            rndElement.style.setProperty('transform', 'initial', 'important');
          }
        }
        
        const domElement = document.querySelector('.cognatesPositionInitial.rnd-no-transform-initial');
        if (domElement && domElement.style && typeof domElement.style.setProperty === 'function') {
          domElement.style.setProperty('transform', 'initial', 'important');
        }
      } catch (error) {
        // Ignore errors during transform setting
      }
    };
    
    forceInitialTransform();
    const intervalId = setInterval(forceInitialTransform, 10);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [hasStartedDragging]);

  return (
    <Rnd
      ref={handleRndRef}
      className={'cognatesPositionInitial rnd-no-transform-initial'}
      style={{ 
        ...(props.style || {}),
        ...(!hasStartedDragging ? { transform: 'initial' } : {})
      }}
      lockAspectRatio={aspectRatio}
      default={{
        x: initialPosition.x,
        y: initialPosition.y,
        width: initialDimensions.width,
        height: initialDimensions.height,
      }}
      size={{
        height: (showAccordin !== undefined && !showAccordin) ? "0px" : dimensions?.height,
        width: dimensions?.width,
      }}
      minWidth={minWidth}
      minHeight={minHeightChecker}
      bounds={bounds}
      disableDragging={isResizing || onFocusValue}
      enableResizing={showAccordin}
      cancel=".no-drag, button, input, select, a, [role='button'], svg, .MuiButton-root, .ant-btn, [data-dropdown], [data-dropdown] *, .dropdown-scrollable, .dropdown-scrollable *"
      enableUserSelectHack={true}
      resizeHandleStyles={aspectRatio ? resizeHandleStyles : {}}
      onResize={(e, direction, ref, delta, position) => {
        if (page === "clock") {
          if (
            direction === "topLeft" ||
            direction === "topRight" ||
            direction === "bottomLeft" ||
            direction === "bottomRight"
          ) {
            setDirectionResizing(true);
          } else {
            setDirectionResizing(false);
          }
        }
        const layout = {
          width: parseInt(ref.style.width, 10),
          height: parseInt(ref.style.height, 10),
          transform: `${Math.min(
            3,
            parseInt(ref.style.width, 10) / minWidth,
            parseInt(ref.style.height, 10) / minHeight
          )}`,
        };
        setDimensions(layout);
        resize(layout);
      }}
      onResizeStart={(e) => {
        setIsResizing(true);
        e?.preventDefault?.();
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
        if (document.selection) {
          document.selection.empty();
        }
      }}
      onResizeStop={() => {
        setIsResizing(false);
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      }}
      onDragStart={(e) => {
        setHasStartedDragging(true);
        setIsDragging(true);
        const existingStyle = document.getElementById('rnd-no-transform-initial');
        if (existingStyle) {
          existingStyle.remove();
        }
        e?.preventDefault?.();
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
        if (document.selection) {
          document.selection.empty();
        }
      }}
      onDragStop={(e, data) => {
        setIsDragging(false);
        setPosition({ x: data.x, y: data.y });
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      }}
      onDrag={(e, data) => {
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
          setPosition({ x: data.x, y: data.y });
        const isDraggingActive = data.deltaX !== 0 || data.deltaY !== 0;
        if (!isDraggingActive) {
          setTimeout(() => {
            document.body.classList.remove('react-draggable-transparent-selection');
          }, 0);
        }
      }}
      {...props}
    >
      <div 
        className="widgetCustomNew"
        style={{ 
          width: "100%", 
          height: "100%",
          userSelect: (isDragging || isResizing) ? "none" : "auto",
          WebkitUserSelect: (isDragging || isResizing) ? "none" : "auto",
          msUserSelect: (isDragging || isResizing) ? "none" : "auto",
          MozUserSelect: (isDragging || isResizing) ? "none" : "auto"
        }}
        onSelectStart={(e) => {
          if (isDragging || isResizing) {
            e.preventDefault();
            return false;
          }
        }}
        onMouseDown={(e) => {
          // Stop propagation for buttons, interactive elements, and dropdowns to prevent dragging
          const target = e.target;
          const isDropdown = target.closest('[data-dropdown]') || target.closest('.dropdown-scrollable');
          if (target.tagName === 'BUTTON' || target.closest('button') || target.tagName === 'INPUT' || target.closest('input') || target.closest('.no-drag') || target.closest('[role="button"]') || isDropdown) {
            e.stopPropagation();
            // Remove all react-draggable classes to allow clicks to work
            document.body.classList.remove('react-draggable-transparent-selection');
            // Also remove classes from any parent elements
            const rndElements = document.querySelectorAll('.react-draggable');
            rndElements.forEach(el => {
              el.classList.remove('react-draggable-dragging');
              el.classList.remove('react-draggable-dragged');
            });
          } else if (isDragging || isResizing) {
            if (window.getSelection) {
              window.getSelection().removeAllRanges();
            }
          }
        }}
        onTouchStart={(e) => {
          // Stop propagation for buttons, interactive elements, and dropdowns to prevent dragging on mobile
          const target = e.target;
          const isDropdown = target.closest('[data-dropdown]') || target.closest('.dropdown-scrollable');
          if (target.tagName === 'BUTTON' || target.closest('button') || target.tagName === 'INPUT' || target.closest('input') || target.closest('.no-drag') || target.closest('[role="button"]') || isDropdown) {
            e.stopPropagation();
            // Remove all react-draggable classes to allow taps to work
            document.body.classList.remove('react-draggable-transparent-selection');
            // Also remove classes from any parent elements
            const rndElements = document.querySelectorAll('.react-draggable');
            rndElements.forEach(el => {
              el.classList.remove('react-draggable-dragging');
              el.classList.remove('react-draggable-dragged');
            });
          }
        }}
        onClick={(e) => {
          // Remove class on click to ensure buttons work
          const target = e.target;
          if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('[role="button"]')) {
            document.body.classList.remove('react-draggable-transparent-selection');
            // Also remove classes from any parent elements
            const rndElements = document.querySelectorAll('.react-draggable');
            rndElements.forEach(el => {
              el.classList.remove('react-draggable-dragging');
              el.classList.remove('react-draggable-dragged');
            });
          }
        }}
      >
        {children}
      </div>
    </Rnd>
  );
};

export default ResizeAndDraggable;
